/**
 * POST-INSTALL-01/06 — the after-care checklist on an install.
 * Redesigned (Seth 2026-08-25 "busy and messy"): one glance = done vs not.
 * Each item is a single calm row — status dot, label, one honest state line,
 * ONE primary action. Manual override + not-applicable live behind the ⋯
 * menu instead of a button pile. Progress bar up top; the two job-level
 * switches (No Permit / Ask for Review) stay at the bottom.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardCheck, Check, CheckCircle2, Clock, Undo2, Send, RefreshCw, Mail, MessageSquare,
  FileUp, FileCheck2, Globe, Minus, MoreHorizontal,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { toastError } from "@/components/ui/mutationToast";
import { uploadFilesTo } from "@/features/files/filesCore";
import { useUtilities } from "@/features/quotes/quotesAdmin";
import {
  usePostInstall, usePatchPostInstall, itemPatch, POST_INSTALL_DEFAULTS,
  useQueuePostInstallSend, usePostInstallOutbox, ITEM_SEND_KEY, useProposalRebate,
  useQueueInstallRecap, useEmailRecapTest, useRecapReadiness,
  type PostInstallItem, type PostInstallRow, type PostInstallStatus, type OutboxTrace,
} from "./postInstall";
import { useCurrentUserEmail } from "@/features/inbox/api";

// PI-05: the dropdown is fed by pegasus_utility — the SAME list the proposal
// builder uses, so both sides stay linked. This is only the offline fallback.
const FALLBACK_UTILITIES = [
  "Xcel Energy", "CenterPoint Energy", "Dakota Electric", "Minnesota Energy",
  "MVEC", "Shakopee Utilities", "Connexus",
];

const ITEMS: { key: PostInstallItem; label: string }[] = [
  { key: "permit", label: "Permit & Inspection Info" },
  { key: "survey", label: "Survey" },
  { key: "referral", label: "Referral Offer" },
  { key: "warranty", label: "Warranty Certificate" },
  { key: "rebate", label: "Utility Rebate" },
];

export function PostInstallCard({ dispatch, jobId, custno, locno }: {
  dispatch?: string | null;
  jobId?: string | null;
  custno: string | null;
  locno: string | null;
}) {
  const key = useMemo(() => ({ dispatch: dispatch ?? null, jobId: jobId ?? null }), [dispatch, jobId]);
  const { data } = usePostInstall(key);
  const patch = usePatchPostInstall();
  // PI-02: the real sends — queued through the ONE comms engine + the full
  // attempt trace (held / queued / sent / failed) right on the card.
  const queue = useQueuePostInstallSend();
  const outbox = usePostInstallOutbox(key).data ?? [];
  const qc = useQueryClient();
  // INSTALL-RECAP-01: the ONE close-out email + its test-to-me.
  const queueRecap = useQueueInstallRecap();
  const recapTest = useEmailRecapTest();
  const myEmail = useCurrentUserEmail().data ?? null;
  const [recapNote, setRecapNote] = useState<string | null>(null);
  // POST-READY-01: the facts the board states before anything sends.
  const ready = useRecapReadiness(key, custno, locno).data ?? null;
  const recapTrace = outbox.find((t) => t.automation_key === "post_install_recap") ?? null;
  // PI-03: the warranty certificate slot — the send is gated on this file.
  const certRef = useRef<HTMLInputElement>(null);
  const [certBusy, setCertBusy] = useState(false);
  const { data: cert } = useQuery({
    queryKey: ["warranty_cert", dispatch ?? null, jobId ?? null],
    enabled: !!(dispatch || jobId) && !!custno,
    queryFn: async (): Promise<{ filename: string } | null> => {
      let q = supabase.from("pegasus_file").select("filename").eq("custno", custno!)
        .eq("doc_tag", "warranty_cert").eq("archived", false)
        .order("created_at", { ascending: false }).limit(1);
      q = jobId ? q.eq("job_id", jobId) : q.eq("dispatch", dispatch!);
      const { data: r, error } = await q.maybeSingle();
      if (error) throw error;
      return (r as { filename: string } | null) ?? null;
    },
  });
  const uploadCert = async (list: FileList | null) => {
    if (!list || list.length === 0 || !custno) return;
    setCertBusy(true);
    try {
      await uploadFilesTo(
        { custno, locno, dispatch: jobId ? null : dispatch, job_id: jobId ?? null, doc_tag: "warranty_cert", caption: "Warranty certificate" },
        [...list],
      );
      void qc.invalidateQueries({ queryKey: ["warranty_cert"] });
    } catch (e) {
      toastError((e as Error).message || "Upload failed.");
    } finally {
      setCertBusy(false);
      if (certRef.current) certRef.current.value = "";
    }
  };
  // PI-03/04: rebate inputs — the confirmation number IS the message.
  const [rebUtilSel, setRebUtilSel] = useState("");   // dropdown value ("" | name | "__other")
  const [rebUtilOther, setRebUtilOther] = useState("");
  const [rebConf, setRebConf] = useState("");
  const [rebAmount, setRebAmount] = useState("");
  const rebUtility = rebUtilSel === "__other" ? rebUtilOther : rebUtilSel;

  // PI-05: the shared dropdown (pegasus_utility — same list the proposal uses).
  const utilityNames = (useUtilities().data ?? []).map((u) => u.name);
  const utilityOptions = utilityNames.length ? utilityNames : FALLBACK_UTILITIES;

  // Render defaults until the row exists — first change creates it.
  const row: Omit<PostInstallRow, "id"> = useMemo(
    () => data ?? { dispatch: dispatch ?? "", custno, locno, ...POST_INSTALL_DEFAULTS },
    [data, dispatch, custno, locno],
  );

  // PI-05: what the sales tech promised on the ACCEPTED proposal option —
  // prefills company + estimated $ so the office isn't retyping the quote.
  const proposalQ = useProposalRebate(jobId);
  const proposal = proposalQ.data ?? null;
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (data === undefined) return;                 // post-install row still loading
    if (jobId && proposalQ.isPending) return;       // proposal answer still loading
    seededRef.current = true;
    const util = row.rebate_utility ?? proposal?.utility ?? null;
    const amt = row.rebate_amount ?? proposal?.amount ?? null;
    if (util) {
      if (utilityOptions.includes(util)) setRebUtilSel(util);
      else { setRebUtilSel("__other"); setRebUtilOther(util); }
    }
    if (amt != null && amt > 0) setRebAmount(String(amt));
    if (row.rebate_confirmation) setRebConf(row.rebate_confirmation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, proposal, proposalQ.isPending, jobId]);

  const apply = async (p: Record<string, unknown>) =>
    patch.mutate({ key, custno, locno, patch: p });

  const setItem = async (item: PostInstallItem, status: PostInstallStatus, extra?: Record<string, unknown>) => {
    const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
    void apply({ ...itemPatch(item, status, who), ...(extra ?? {}) });
  };

  const doSend = (item: PostInstallItem, resend: boolean) => {
    if (!custno) { toastError("No customer on this install — can't send."); return; }
    const utility = item === "rebate" ? (rebUtility.trim() || row.rebate_utility || "") : undefined;
    const conf = item === "rebate" ? (rebConf.trim() || row.rebate_confirmation || "") : undefined;
    const amount = item === "rebate" ? (rebAmount.trim() ? Number(rebAmount.replace(/[^0-9.]/g, "")) || null : row.rebate_amount) : undefined;
    queue.mutate(
      {
        sendKey: ITEM_SEND_KEY[item], key, custno, locno, reviewsEnabled: row.reviews_enabled, resend,
        rebateUtility: utility, rebateConfirmation: conf, rebateAmount: amount ?? null,
      },
      {
        onSuccess: async (r) => {
          if (item === "rebate") void apply({ rebate_utility: utility || null, rebate_confirmation: conf || null, rebate_amount: amount ?? null });
          if (!resend) void setItem(item, "sent", item === "warranty" ? { warranty_method: "uploaded" } : undefined);
          void r;
        },
        onError: (e) => toastError((e as Error).message || "Couldn't queue the send."),
      },
    );
  };

  const traceFor = (item: PostInstallItem): OutboxTrace | null =>
    outbox.find((t) => t.automation_key === ITEM_SEND_KEY[item]) ?? null;

  // The two job-level switches imply item states.
  const effectiveStatus = (item: PostInstallItem): PostInstallStatus => {
    if (item === "permit" && !row.permit_required) return "not_needed";
    if (item === "survey" && !row.reviews_enabled) return "not_needed";
    return row[`${item}_status`];
  };

  const doneCount = ITEMS.filter(({ key: k }) => effectiveStatus(k) !== "pending").length;
  const allDone = doneCount === ITEMS.length;
  const fmtDay = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  // POST-READY-01 — what's still missing before the recap can carry everything.
  // (The rebate never blocks: it's its own later send once the confirmation
  // number exists.)
  const waiting: string[] = [];
  if (ready) {
    if (!ready.toEmail) waiting.push(ready.emailReason === "opted_out" ? "an email path (customer opted out)" : "a customer email address");
    if (row.permit_required && ready.permits.length === 0) waiting.push("permit numbers");
    if (effectiveStatus("warranty") === "pending" && !cert) waiting.push("the warranty certificate");
  }
  const allReady = ready != null && waiting.length === 0;
  const recapQueuedOrSent = !!recapTrace && ["sent", "queued", "held"].includes(recapTrace.status);

  return (
    <div>
      {/* Header — the whole story in one line + a bar. */}
      <div className="mb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-brand-600" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Post-Install</h3>
            <InfoTip label="How post-install sends work">
              <b>Send</b> delivers through Pegasus — text or email by each customer&rsquo;s preference, held while
              comms are in testing. The <b>⋯</b> menu records manual sends and not-applicable items.
            </InfoTip>
          </div>
          <span className={cn("text-[13px] font-bold", allDone ? "text-emerald-600" : "text-slate-500")}>
            {allDone ? "All done" : `${doneCount} of ${ITEMS.length} done`}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${(doneCount / ITEMS.length) * 100}%` }} />
        </div>
      </div>

      {/* POST-READY-01 (Seth 2026-10-05: "I don't know what will send
          automatically — and when. Or if I need to send manually. Feels
          squished."): the board LEADS with the plan, in words. One glance
          answers: what's ready, who it goes to, what's still missing, what
          already went out. The recap is THE send; one-offs live behind ⋯. */}
      {custno && (
        <div className={cn(
          "mb-4 rounded-xl border p-3.5",
          recapQueuedOrSent ? "border-sky-200 bg-sky-50/50"
            : !ready ? "border-hairline bg-slate-50"
            : allReady ? "border-emerald-200 bg-emerald-50/50"
            : "border-amber-200 bg-amber-50/60",
        )}>
          <div className="flex items-start gap-2.5">
            {recapQueuedOrSent
              ? <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              : allReady
                ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                : <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  "text-[13.5px] font-bold",
                  recapQueuedOrSent ? "text-sky-900" : allReady ? "text-emerald-900" : "text-slate-800",
                )}>
                  {recapTrace
                    ? recapTrace.status === "sent" ? `Close-out recap sent ${fmtDay(recapTrace.sent_at)}`
                      : recapTrace.status === "held" ? "Close-out recap is queued — held while comms are in testing"
                      : recapTrace.status === "queued" ? "Close-out recap is queued — sends within ~10 minutes"
                      : recapTrace.status === "failed" ? "Close-out recap FAILED — see the outbox"
                      : "Close-out recap"
                    : !ready ? "Checking what's ready…"
                    : allReady ? "Everything's ready for the close-out recap"
                    : `Waiting on ${waiting.join(" and ")}`}
                </span>
                <InfoTip label="How the close-out send works">
                  ONE recap email carries everything: the equipment with model, serial and warranty dates, the
                  permit numbers with the city&rsquo;s own instructions and real deadline, warranty certificate
                  status, the survey and the referral offer. An item switched off (&ldquo;Not Needed&rdquo;) drops
                  out of the email. The OFFICE sends it when everything&rsquo;s in — nothing goes by itself — and
                  if it still hasn&rsquo;t gone five days after the install wraps, it shows up on Today as a
                  reminder. Anything finished later (a late certificate, the rebate confirmation) follows as its
                  own email.
                </InfoTip>
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {recapTrace
                  ? <>To <b className="text-slate-700">{recapTrace.to_address ?? ready?.toEmail ?? "—"}</b> · items finished later follow as their own emails</>
                  : ready?.toEmail
                    ? <>Goes to <b className="text-slate-700">{ready.toEmail}</b>{!allReady && " — you can queue it now with what's ready; the rest follows later"}</>
                    : ready
                      ? (ready.emailReason === "opted_out" ? "This customer has email turned off — the recap can't send." : "No customer email on file yet.")
                      : null}
              </div>
            </div>
          </div>
          {!recapQueuedOrSent && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button type="button" disabled={queueRecap.isPending || !ready?.toEmail}
                onClick={() =>
                  queueRecap.mutate(
                    {
                      key, custno, locno, reviewsEnabled: row.reviews_enabled, permitRequired: row.permit_required,
                      includeSurvey: effectiveStatus("survey") !== "not_needed",
                      includeReferral: effectiveStatus("referral") !== "not_needed",
                    },
                    {
                      onSuccess: (r) => setRecapNote(r.held
                        ? `Held in the outbox for ${r.to} — it sends when comms go live.`
                        : `Queued to ${r.to}.`),
                      onError: (e) => toastError((e as Error).message),
                    },
                  )
                }
                title={allReady ? "Queue the full recap" : "Queues with what's ready — anything missing follows later as its own email"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
                  allReady
                    ? "bg-brand-700 text-white hover:bg-brand-800 disabled:bg-brand-300"
                    : "border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50",
                )}>
                {queueRecap.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {allReady ? "Queue the recap" : "Queue with what's ready"}
              </button>
              <button type="button" disabled={recapTest.isPending || !myEmail}
                onClick={() =>
                  recapTest.mutate(
                    {
                      key, custno, locno, reviewsEnabled: row.reviews_enabled, permitRequired: row.permit_required,
                      includeSurvey: effectiveStatus("survey") !== "not_needed",
                      includeReferral: effectiveStatus("referral") !== "not_needed",
                      myEmail: myEmail!,
                    },
                    {
                      onSuccess: (c) => setRecapNote(`Test sent to ${myEmail} — ${c.units} unit${c.units === 1 ? "" : "s"}, ${c.permits} permit${c.permits === 1 ? "" : "s"}, certificate ${c.certIncluded ? "included" : "pending"}.`),
                      onError: (e) => toastError((e as Error).message),
                    },
                  )
                }
                title="The exact recap, real data, to YOUR inbox — never the customer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50">
                {recapTest.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                Email me a test
              </button>
            </div>
          )}
          {recapNote && <div className="mt-1.5 text-[11.5px] font-medium text-emerald-700">{recapNote}</div>}
        </div>
      )}

      <ul className="space-y-2">
        {ITEMS.map(({ key: k, label }) => {
          const status = effectiveStatus(k);
          const lockedBySwitch =
            (k === "permit" && !row.permit_required) || (k === "survey" && !row.reviews_enabled);
          const sentAt = row[`${k}_sent_at`];
          const sentBy = row[`${k}_sent_by`];
          const trace = traceFor(k);
          const onlineNoCert = k === "warranty" && row.warranty_method === "online_no_cert";
          const rebConfReady = k !== "rebate" || !!(rebConf.trim() || row.rebate_confirmation);
          const sendGate =
            k === "warranty" && !cert ? "Upload the certificate first — the email links straight to it"
            : k === "rebate" && !rebConfReady ? "Enter the confirmation number first — it IS the message"
            : null;

          // The ⋯ menu — manual override + not-applicable, tucked away.
          // POST-READY-01: per-item sending moved HERE — the recap is the
          // send; a separate one-off is the exception, not the default.
          const menu: { label: string; icon: ReactNode; onClick: () => void }[] = [];
          if (status === "pending") {
            if (k !== "rebate" && !recapQueuedOrSent && !sendGate) menu.push({
              label: "Send Separately Now", icon: <Send className="h-4 w-4 text-brand-600" />,
              onClick: () => doSend(k, false),
            });
            menu.push({
              label: "Mark Sent Manually", icon: <Check className="h-4 w-4 text-emerald-600" />,
              onClick: () => void setItem(k, "sent", k === "warranty" && cert ? { warranty_method: "uploaded" } : undefined),
            });
            if (k === "warranty" && !cert) menu.push({
              label: "Registered Online — No Certificate", icon: <Globe className="h-4 w-4 text-sky-600" />,
              onClick: () => void setItem(k, "not_needed", { warranty_method: "online_no_cert" }),
            });
            if (!lockedBySwitch) menu.push({
              label: k === "rebate" ? "Not Needed / Not Eligible" : "Not Needed",
              icon: <Minus className="h-4 w-4 text-slate-400" />,
              onClick: () => void setItem(k, "not_needed", k === "warranty" ? { warranty_method: null } : undefined),
            });
          } else if (!lockedBySwitch) {
            menu.push({
              label: "Undo — Back to Pending", icon: <Undo2 className="h-4 w-4 text-slate-400" />,
              onClick: () => void setItem(k, "pending", k === "warranty" ? { warranty_method: null } : undefined),
            });
          }

          return (
            <li key={k} className={cn(
              "flex items-start gap-3 rounded-xl px-3.5 py-3",
              status === "sent" ? "border border-emerald-100 bg-emerald-50/50"
              : status === "not_needed" ? "border border-transparent bg-slate-50"
              : "border border-hairline bg-white",
            )}>
              <StatusDot status={status} online={onlineNoCert} />

              <span className="min-w-0 flex-1">
                <span className={cn("block text-[15px] font-semibold", status === "not_needed" && !onlineNoCert ? "text-slate-500" : "text-slate-800")}>
                  {label}
                </span>

                {/* One honest state line per row. */}
                {status === "sent" && (trace
                  ? <TraceLine t={trace} />
                  : sentAt && (
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Marked sent {fmtDay(sentAt)}{sentBy ? ` · ${sentBy.split("@")[0]}` : ""}
                    </span>
                  ))}
                {status === "pending" && trace && (trace.status === "failed" || trace.status === "suppressed") && <TraceLine t={trace} />}

                {/* POST-READY-01: the FACTS already on file, before anything sends. */}
                {status === "pending" && k === "permit" && ready && (
                  ready.permits.length > 0 ? (
                    <span className="mt-1 block text-xs font-medium leading-relaxed text-slate-600">
                      On file: <b className="text-slate-700">{ready.permits.join(" · ")}</b>
                      {ready.deadline ? <span className="text-slate-400"> — inspection window ends {ready.deadline}</span> : null}
                      {ready.electricalClosed ? <span className="font-semibold text-emerald-600"> · electrical already closed on-site</span> : null}
                    </span>
                  ) : (
                    <span className="mt-1 block text-xs font-medium text-amber-600">
                      No permit numbers on file yet — they ride in automatically once entered{jobId ? " on the Permits tab" : ""}.
                    </span>
                  )
                )}
                {status === "pending" && (k === "survey" || k === "referral") && !recapQueuedOrSent && (
                  <span className="mt-1 block text-xs text-slate-400">Rides in the recap — nothing to prepare.</span>
                )}
                {status === "not_needed" && (
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {onlineNoCert ? "Registered online with the manufacturer — no certificate to send."
                      : lockedBySwitch ? "Turned off by the switch below."
                      : k === "rebate" ? "Not needed or not eligible." : "Not needed for this customer."}
                  </span>
                )}
                {status === "sent" && k === "rebate" && (row.rebate_confirmation || row.rebate_utility) && (
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {row.rebate_utility ?? "Utility"} · conf <b className="tracking-wide text-slate-700">#{row.rebate_confirmation ?? "—"}</b>
                    {row.rebate_amount ? ` · est. ${Number(row.rebate_amount).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}` : ""}
                  </span>
                )}

                {/* What this row still needs — only while pending. */}
                {k === "warranty" && status === "pending" && (
                  <span className="mt-2 flex flex-wrap items-center gap-1.5">
                    {cert ? (
                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" title={cert.filename}>
                        <FileCheck2 className="h-3.5 w-3.5" /> Certificate on File
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-amber-600">Upload the certificate — the recap carries the download button once it&rsquo;s on file.</span>
                    )}
                    <button type="button" onClick={() => certRef.current?.click()} disabled={certBusy}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-hairline transition-colors hover:bg-slate-50 disabled:opacity-50">
                      <FileUp className="h-3.5 w-3.5" /> {certBusy ? "Uploading…" : cert ? "Replace" : "Upload Certificate"}
                    </button>
                    <input ref={certRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => void uploadCert(e.target.files)} />
                  </span>
                )}
                {k === "rebate" && status === "pending" && (
                  <span className="mt-2 block space-y-2">
                    {proposal && (
                      <span className="block text-xs font-medium text-sky-700">
                        Promised on the proposal: <b>{proposal.utility ?? "utility"}</b>
                        {proposal.amount ? <> · est. {Number(proposal.amount).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}</> : null}
                        <span className="font-normal text-slate-400"> — edit below if the sales tech had it wrong.</span>
                      </span>
                    )}
                    <span className="flex flex-wrap items-center gap-2">
                      <select value={rebUtilSel} onChange={(e) => setRebUtilSel(e.target.value)}
                        className="h-9 rounded-lg border border-hairline bg-white px-2.5 text-sm text-slate-700 focus:border-brand-400 focus:outline-none">
                        <option value="">Utility…</option>
                        {utilityOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                        <option value="__other">Other…</option>
                      </select>
                      {rebUtilSel === "__other" && (
                        <input value={rebUtilOther} onChange={(e) => setRebUtilOther(e.target.value)}
                          placeholder="Utility name"
                          className="h-9 w-40 rounded-lg border border-hairline px-2.5 text-sm focus:border-brand-400 focus:outline-none" />
                      )}
                      <span className="relative">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                        <input value={rebAmount} onChange={(e) => setRebAmount(e.target.value)}
                          placeholder="Est. amount" inputMode="decimal"
                          className="h-9 w-28 rounded-lg border border-hairline py-0 pl-6 pr-2.5 text-sm tabular-nums focus:border-brand-400 focus:outline-none" />
                      </span>
                      <input value={rebConf} onChange={(e) => setRebConf(e.target.value)}
                        placeholder="Confirmation #"
                        className="h-9 w-40 rounded-lg border border-hairline px-2.5 text-sm font-medium tracking-wide focus:border-brand-400 focus:outline-none" />
                    </span>
                  </span>
                )}
              </span>

              {/* POST-READY-01: the recap is the send. A visible button stays
                  only where a SEPARATE send is the real path — the rebate
                  (its own later track) and stragglers after the recap went. */}
              <span className="flex shrink-0 items-center gap-1">
                {status === "pending" && (k === "rebate" || recapQueuedOrSent) && (
                  <button type="button"
                    title={sendGate ?? (k === "rebate"
                      ? "Queue the rebate email (held until comms go live)"
                      : "The recap already went — this sends as its own follow-up email")}
                    disabled={queue.isPending || patch.isPending || !!sendGate}
                    onClick={() => doSend(k, false)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-bold transition-colors",
                      sendGate ? "cursor-not-allowed bg-slate-100 text-slate-400"
                        : "bg-brand-700 text-white hover:bg-brand-800 disabled:bg-brand-300",
                    )}>
                    <Send className="h-3.5 w-3.5" /> {k === "rebate" ? "Send" : "Send Follow-Up"}
                  </button>
                )}
                {status === "sent" && (
                  <button type="button" title="Customer needs it again — queue another copy" disabled={queue.isPending}
                    onClick={() => doSend(k, true)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100">
                    <RefreshCw className="h-3.5 w-3.5" /> Resend
                  </button>
                )}
                <RowMenu items={menu} disabled={patch.isPending} />
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
        <div className="flex items-center gap-1.5">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={!row.permit_required} disabled={patch.isPending}
              onChange={(e) => void apply({ permit_required: !e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40" />
            No Permit Needed
          </label>
          <InfoTip label="What No Permit Needed does">Permit &amp; inspection info won&rsquo;t be sent for this install, and it drops out of the recap.</InfoTip>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={row.reviews_enabled} disabled={patch.isPending}
              onChange={(e) => void apply({ reviews_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40" />
            Ask for a Review / Survey
          </label>
          <InfoTip label="About the review ask">Off means the survey and Google-review lines stay out of every send. Techs can quietly flip this from the field app.</InfoTip>
        </div>
      </div>

      {patch.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — try again.</p>}
    </div>
  );
}

/** The at-a-glance circle: green check = sent, globe = registered online,
 *  dash = not needed, dashed empty ring = still to do. */
function StatusDot({ status, online }: { status: PostInstallStatus; online: boolean }) {
  if (status === "sent") return (
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
      <Check className="h-4 w-4" strokeWidth={3} />
    </span>
  );
  if (status === "not_needed") return online ? (
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700" title="Registered online — no certificate">
      <Globe className="h-4 w-4" />
    </span>
  ) : (
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500">
      <Minus className="h-4 w-4" />
    </span>
  );
  return <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full border-2 border-dashed border-slate-300 bg-white" />;
}

/** The quiet ⋯ menu — manual override + not-applicable options. */
function RowMenu({ items, disabled }: {
  items: { label: string; icon: ReactNode; onClick: () => void }[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <span className="relative">
      <button type="button" title="Manual override / not applicable" disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "rounded-lg p-1.5 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40",
          open ? "bg-slate-100 text-slate-600" : "text-slate-400",
        )}>
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-10 cursor-default" aria-label="Close menu"
            onClick={() => setOpen(false)} tabIndex={-1} />
          <span className="absolute right-0 top-9 z-20 block w-64 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-lg">
            {items.map((it) => (
              <button key={it.label} type="button"
                onClick={() => { setOpen(false); it.onClick(); }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-50">
                {it.icon}{it.label}
              </button>
            ))}
          </span>
        </>
      )}
    </span>
  );
}

/** The newest send attempt for one item — the honest "what actually happened". */
function TraceLine({ t }: { t: OutboxTrace | null }) {
  if (!t) return null;
  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  const Icon = t.channel === "sms" ? MessageSquare : Mail;
  const label =
    t.status === "sent" ? `sent ${when(t.sent_at)}`
    : t.status === "queued" ? "queued — sends within ~10 min"
    : t.status === "held" ? `queued ${when(t.created_at)} · held (comms in testing)`
    : t.status === "failed" ? `failed — ${t.status_detail ?? "unknown"}`
    : t.status === "suppressed" ? `not sent — ${t.status_detail ?? "suppressed"}`
    : t.status;
  const tone =
    t.status === "sent" ? "text-emerald-600"
    : t.status === "failed" ? "text-rose-600"
    : t.status === "held" ? "text-amber-600"
    : t.status === "suppressed" ? "text-slate-400"
    : "text-sky-600";
  return (
    <span className={"mt-1 flex items-center gap-1.5 text-xs font-medium " + tone} title={t.to_address ?? undefined}>
      <Icon className="h-3.5 w-3.5 shrink-0" /> {label}
    </span>
  );
}
